import React from 'react';
import { Sparkles, HeartPulse, Smile, Sparkle, Home, Coffee, Sprout } from 'lucide-react';
import { CATEGORIES } from '../data/products';

interface CategoryNavProps {
  selectedCategory: string;
  onSelectCategory: (id: string) => void;
}

const iconMap: Record<string, React.ReactNode> = {
  Sparkles: <Sparkles className="w-5 h-5" />,
  HeartPulse: <HeartPulse className="w-5 h-5" />,
  Smile: <Smile className="w-5 h-5" />,
  Sparkle: <Sparkle className="w-5 h-5" />,
  Home: <Home className="w-5 h-5" />,
  Coffee: <Coffee className="w-5 h-5" />,
  Sprout: <Sprout className="w-5 h-5" />,
};

export const CategoryNav: React.FC<CategoryNavProps> = ({
  selectedCategory,
  onSelectCategory,
}) => {
  return (
    <div className="my-2 sm:my-4">
      {/* White Glass Bubble Background Container (บับเบิ้ลแก้ว สีขาว) */}
      <div className="relative rounded-[26px] sm:rounded-[30px] bg-white/75 backdrop-blur-md border border-white/90 shadow-md shadow-pink-500/5 p-4 sm:p-5 overflow-hidden transition-all duration-300">
        
        {/* Ambient Top Light Flare on Glass */}
        <div className="absolute top-0 left-1/4 w-1/2 h-1 bg-gradient-to-r from-transparent via-white to-transparent opacity-80 pointer-events-none" />
        
        {/* Header Strip */}
        <div className="relative rounded-2xl bg-gradient-to-r from-blue-50/80 via-white to-indigo-50/80 border border-blue-200/60 shadow-xs px-3.5 py-2.5 sm:px-4.5 sm:py-3 mb-4 flex items-center justify-between overflow-hidden">
          {/* Subtle Sheen Reflection */}
          <div className="absolute inset-0 bg-gradient-to-r from-white/60 via-blue-50/20 to-white/40 pointer-events-none" />
          <div className="absolute top-0 left-0 right-0 h-[1px] bg-gradient-to-r from-transparent via-white to-transparent" />

          {/* Left: Title with Glowing Blue Dot */}
          <div className="relative z-10 flex items-center gap-2 sm:gap-2.5">
            <span className="w-3 h-3 rounded-full bg-gradient-to-r from-[#2A59FE] to-[#4F46E5] shadow-xs shadow-blue-500/40 ring-2 ring-blue-100 animate-pulse shrink-0" />
            <h2 className="text-sm sm:text-base font-extrabold text-slate-800 tracking-tight">
              หมวดหมู่ผลิตภัณฑ์กิฟฟารีน
            </h2>
          </div>

          {/* Right: View All by Category text */}
          <span className="relative z-10 text-xs sm:text-[12.5px] text-[#2A59FE] font-bold hover:text-[#1E3A8A] hover:underline cursor-pointer transition-colors shrink-0">
            เลือกดูตามหมวด
          </span>
        </div>

        {/* Metallic Shiny Bubble Navigation inside White Glass Card */}
        <div className="flex gap-4 sm:gap-5 overflow-x-auto pb-2 pt-1 px-1 scrollbar-none sm:grid sm:grid-cols-4 md:grid-cols-7 sm:overflow-visible">
          {CATEGORIES.map((cat) => {
            const isSelected = selectedCategory === cat.id;
            return (
              <button
                key={cat.id}
                id={`cat-bubble-${cat.id}`}
                onClick={() => onSelectCategory(cat.id)}
                className="flex flex-col items-center gap-2 shrink-0 w-22 sm:w-auto text-center group cursor-pointer transition-transform duration-200 hover:-translate-y-1"
              >
                {/* Metallic Shiny Bubble Sphere */}
                <div
                  className={`relative w-16 h-16 sm:w-18 sm:h-18 rounded-full flex items-center justify-center transition-all duration-300 ${
                    isSelected
                      ? 'bg-gradient-to-tr from-[#2A59FE] via-[#3B66FE] to-[#4F46E5] text-white shadow-lg shadow-blue-600/35 border-2 border-white/90 scale-105 ring-3 ring-blue-400/40'
                      : 'bg-[radial-gradient(ellipse_at_30%_25%,_#FFFFFF_0%,_#F1F5F9_45%,_#CBD5E1_100%)] text-slate-700 shadow-md shadow-slate-400/20 border-2 border-white hover:border-blue-300 group-hover:shadow-blue-300/30'
                  }`}
                >
                  {/* 3D Specular Bubble Light Flare (Top Curved Highlight) */}
                  <div className="absolute top-1.5 left-2.5 w-6 h-3 rounded-full bg-white/70 blur-[0.5px] -rotate-25 pointer-events-none" />
                  {/* Secondary Bottom Rim Reflection */}
                  <div className="absolute bottom-1 right-2.5 w-4 h-1.5 rounded-full bg-white/40 blur-[0.6px] pointer-events-none" />

                  {/* Inner Icon */}
                  <div
                    className={`relative z-10 transition-transform duration-200 group-hover:scale-110 drop-shadow-sm ${
                      isSelected ? 'text-white' : 'text-slate-700 group-hover:text-[#2A59FE]'
                    }`}
                  >
                    {iconMap[cat.icon] || <Sparkles className="w-6 h-6" />}
                  </div>
                </div>

                {/* Category Name Label */}
                <span
                  className={`text-[12px] sm:text-[12.5px] font-semibold leading-tight px-1 transition-colors ${
                    isSelected
                      ? 'text-[#2A59FE] font-bold'
                      : 'text-slate-700 group-hover:text-blue-600'
                  }`}
                >
                  {cat.name}
                </span>
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
};
